import * as React from 'react';
import { Dispatch } from 'react-redux';
import { StoreState } from '../../../reducers';
import { MenuItemType, MenuItem, MenuPosition, CreateButtonOption } from '../../../typings/menu';
import { StyledLeftSideMenu, HeaderMenuItem, MenuItemsWrapper, UserInfoWrapper } from './styles';
import RoutesUtils from '../../../utils/routes';
import { MenuActionKeys } from '../../../containers/MenuManager/actions';
import { PageOverlay } from '../../PageOverlay/styles';
import { DriveItemListOrigin } from '../../../containers/DriveItemListPage/constants';
import LeftMenuItem from '../../../containers/MenuManager/Left/MenuItem';
import HeaderLogo from '../../../containers/MenuManager/Header/Logo';
import UserAvatar from '../../../containers/ProfilePage/UserAvatar';
import { AvatarSize } from '../../../containers/ProfilePage/reducer';
import UserInfo from '../../../containers/ProfilePage/UserInfo';
import ButtonCreate from '../../../containers/ButtonCreate';

export interface LeftSideMenuProps {
	filteredMenuItems: MenuItem[];
	newItemsNb: number;
	isOpen: boolean;
	hasLogOutButton: boolean;
	hasProfile: boolean;
	hasProposalStatsPage: boolean;
	privateDirId: number | undefined;
	sharedDirId: number | undefined;
	isProfileLoaded: boolean;
	dispatch: Dispatch<StoreState>;
}

class LeftSideMenu extends React.Component<LeftSideMenuProps, {}> {

	constructor(props: LeftSideMenuProps) {
		super(props);
		this.getMenuItems = this.getMenuItems.bind(this);
	}

	renderMenuItem = (item: MenuItem, index: number) => {
		if (item) {
			return (
				<LeftMenuItem
					menuItem={item}
					key={index}
					badge={item.badge}
				/>
			);
		}

		return null;
	}

	render() {
		const { isOpen, filteredMenuItems, hasProfile, isProfileLoaded } = this.props;
		const menuItems = this.getMenuItems(filteredMenuItems);

		return (
			<>
				<PageOverlay isOpen={isOpen} onClick={this.handleOverlayClick} />
				<StyledLeftSideMenu isOpen={isOpen}>
					<MenuItemsWrapper>
						<HeaderMenuItem hasProfile={hasProfile && isProfileLoaded}>
							{hasProfile && isProfileLoaded
								? (
									<UserInfoWrapper>
										<UserAvatar size={AvatarSize.SMALL} />
										<UserInfo isSmall={true} />
									</UserInfoWrapper>
								)
								: <HeaderLogo />
							}
						</HeaderMenuItem>
						{menuItems}
					</MenuItemsWrapper>
				</StyledLeftSideMenu>
			</>
		);
	}

	handleOverlayClick = () => {
		const { dispatch } = this.props;
		dispatch({ type: MenuActionKeys.REQUEST_CLOSE_LEFT_MENU });
	}

	getMenuItems = (items: MenuItem[]) => {
		const {
			hasLogOutButton,
			hasProfile,
			hasProposalStatsPage,
			privateDirId,
			sharedDirId,
			dispatch,
		} = this.props;

		if (hasProposalStatsPage) {
			const proposalStatsButton = { type: MenuItemType.ProposalStats } as MenuItem;
			items.push(proposalStatsButton);
		}

		if (hasProfile) {
			const profileButton = { type: MenuItemType.Profile } as MenuItem;
			items.push(profileButton);
		}

		if (hasLogOutButton) {
			const logOutButtonItem = { type: MenuItemType.LogOut } as MenuItem;
			items.push(logOutButtonItem);
		}

		return items.map((item, index) => {
			let itemArgs;

			switch (item.type) {
				case MenuItemType.Home:
					itemArgs = {
						type: MenuItemType.Home,
						label: 'messages.home',
						icon: 'iconset_home',
						target: RoutesUtils.getPathHome()
					};
					break;
				case MenuItemType.Gallery:
					itemArgs = {
						type: MenuItemType.Gallery,
						label: 'messages.my_gallery',
						icon: 'iconset_image',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Folder, null)
					};
					break;
				case MenuItemType.New:
					itemArgs = {
						type: MenuItemType.New,
						label: 'messages.new',
						icon: 'iconset_uprez_menu_news',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.New, null),
						badge: this.props.newItemsNb
					};
					break;
				case MenuItemType.Selection:
					itemArgs = {
						type: MenuItemType.Selection,
						label: 'messages.my_selection',
						icon: 'iconset_uprez_menu_selection',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Selection, null)
					};
					break;
				case MenuItemType.Downloads:
					itemArgs = {
						type: MenuItemType.Downloads,
						label: 'messages.my_downloads',
						icon: 'iconset_uprez_download_bold',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Downloads, null)
					};
					break;
				case MenuItemType.SharedWithMe:
					itemArgs = {
						type: MenuItemType.SharedWithMe,
						label: 'messages.shared_with_me',
						icon: 'iconset_shared_with_me',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Folder, String(sharedDirId))
					};
					break;
				case MenuItemType.Documents:
					itemArgs = {
						type: MenuItemType.Documents,
						label: 'messages.my_documents',
						icon: 'iconset_uprez_menu_folder',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Folder, String(privateDirId)),
					};
					break;
				case MenuItemType.Parameters:
					itemArgs = {
						type: MenuItemType.Parameters,
						label: 'messages.parameters',
						icon: 'iconset_parameters',
						target: RoutesUtils.getPathParameters()
					};
					break;
				case MenuItemType.Profile:
					itemArgs = {
						type: MenuItemType.Profile,
						label: 'messages.profile',
						icon: 'iconset_uprez_profile',
						target: RoutesUtils.getPathProfile(),
					};
					break;
				case MenuItemType.ProposalStats:
					itemArgs = {
						type: MenuItemType.ProposalStats,
						label: 'messages.my_sent',
						icon: 'iconset_uprez_send',
						target: RoutesUtils.getPathProposalStats(),
					};
					break;
				case MenuItemType.SearchButton:
					itemArgs = {
						type: MenuItemType.SearchButton,
						label: 'messages.search',
						icon: 'iconset_uprez_menu_search',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Search, null),
					};
					break;
				case MenuItemType.Link:
					itemArgs = {
						type: MenuItemType.Link,
						label: item.label ? item.label : 'Link',
						icon: item.icon ? item.icon : 'iconset_link',
						target: item.target
					};
					break;
				case MenuItemType.Asset:
					itemArgs = {
						type: MenuItemType.Asset,
						label: item.label ? item.label : 'Asset',
						icon: item.icon ? item.icon : 'iconset_file',
						target: item.target
					};
					break;
				case MenuItemType.Folder:
					itemArgs = {
						type: MenuItemType.Folder,
						label: item.label ? item.label : 'Folder',
						icon: item.icon ? item.icon : 'iconset_folder',
						target: item.target
					};
					break;
				case MenuItemType.Page:
					itemArgs = {
						type: MenuItemType.Page,
						label: item.label ? item.label : 'Page',
						icon: item.icon ? item.icon : 'iconset_page',
						target: item.target,
					};
					break;
				case MenuItemType.LogOut:
					itemArgs = {
						type: MenuItemType.LogOut,
						label: 'messages.log_out',
						icon: 'iconset_logout',
						target: ''
					};
					break;
				case MenuItemType.CreateButton:
					return (
						<ButtonCreate
							key={MenuItemType.CreateButton}
							position={MenuPosition.burger}
							options={item.options as CreateButtonOption[]}
							onOptionClick={() => dispatch({ type: MenuActionKeys.REQUEST_CLOSE_LEFT_MENU })}
						/>
					);
			}
			return this.renderMenuItem(itemArgs, index);
		});
	}
}

export default LeftSideMenu;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/MenuManager/Left/index.tsx